import type { ConversationMessage } from "./memory";
import type { StoryMessageDocument } from "./product";

export interface OocPresetDocument {
  id: string;
  title: string;
  text: string;
}

export const gameplayPrompts = {
  continue: "ดำเนินฉากต่อจากสถานะล่าสุดโดยถือว่าตัวละครผู้เล่นยังไม่มี Action ใหม่ ห้ามควบคุม พูด คิด ตัดสินใจ หรือหลบหลีกแทนตัวละครผู้เล่น ให้โลกและเวลาเคลื่อนไปตามบริบทผ่านการกระทำหรือบทสนทนาของตัวละครอื่น สภาพแวดล้อม และเหตุการณ์ที่กำลังดำเนินอยู่ ตัวละครผู้เล่นอาจเพียงอยู่เฉย ๆ สังเกตการณ์ ดื่มด่ำกับสถานการณ์ ไม่ทำอะไร ไม่หลบ ไม่มีส่วนร่วม หรือเป็นองค์ประกอบเสริมของฉากนี้ตามบริบท เขียนเป็น prose ตามที่กำหนด",
  regenerate: "สร้างคำตอบใหม่จากสถานการณ์เดิมทันที เขียนเฉพาะเนื้อเรื่องและห้ามกล่าวถึงคำสั่งนี้",
  cutRecovery: "stream ก่อนหน้าจบเพราะชนเพดานหรือถูกตัดผิดปกติ ดำเนินคำตอบเดิมต่อจากอักขระสุดท้ายทันที หากยังไม่มีเนื้อหาให้ตอบคำสั่งเดิมโดยไม่เกริ่น ไม่เริ่มฉากใหม่ และไม่ทวนหรือสรุปซ้ำ เขียนร้อยแก้วต่อทันที โดยไม่สร้าง Header ซ้ำและไม่ใช้แท็ก HTML/XML",
} as const;

const blockTags = "address|article|aside|blockquote|div|dl|fieldset|figcaption|figure|footer|form|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul";
const namedEntities: Record<string, string> = { amp: "&", apos: "'", gt: ">", lt: "<", nbsp: " ", quot: '"' };

function decodeNarrationEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|amp|apos|gt|lt|nbsp|quot);/gi, (match, entity: string) => {
    const normalized = entity.toLowerCase();
    if (normalized.startsWith("#x")) {
      const code = Number.parseInt(normalized.slice(2), 16);
      return Number.isFinite(code) && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    if (normalized.startsWith("#")) {
      const code = Number.parseInt(normalized.slice(1), 10);
      return Number.isFinite(code) && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    return namedEntities[normalized] ?? match;
  });
}

/**
 * Converts provider output into the product's native narration format. This is
 * intentionally separate from LegacyRichText, which remains able to display
 * old Android records containing HTML without teaching new runs to emit it.
 */
export function normalizeVisibleNarration(source: string, options: { streaming?: boolean } = {}): string {
  let value = source.replace(/\r\n?/g, "\n");
  value = value.split(/\[AGENT_TOOL_(?:CALL|RESULT)\]/i)[0] ?? "";
  value = value.replace(/<(think|thought|analysis|reasoning)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi, "");
  value = value.split(/---\s*STARTING POSTPROCESSING\s*---/i)[0] ?? "";
  value = value.replace(/\[(?:THOUGHT|ACTION_RESULT|ACTION):[\s\S]*?(?:\]|$)/gi, "");
  if (options.streaming) value = value.replace(/(?:---\s*(?:STARTING(?:\s+POSTPROCESSING?)?)?|<(?:thi?n?k?|thou?g?h?t?|anal?y?s?i?s?|reas?o?n?i?n?g?)?)$/i, "");
  value = value.replace(/<!--[\s\S]*?-->/g, "");
  value = value.replace(/<(script|style|iframe|object|svg|math)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, "");
  value = value.replace(/<br\s*\/?>/gi, "\n");
  value = value.replace(new RegExp(`<\/?(?:${blockTags})\\b[^>]*>`, "gi"), "\n");
  value = value.replace(/<\/?[a-z][^<>]*>/gi, "");
  if (options.streaming) value = value.replace(/<\/?[a-z][^>]*$/i, "");
  value = decodeNarrationEntities(value);
  return value.replace(/[ \t]+\n/g, "\n").replace(/\n[ \t]+/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

export const frequentOocPresets: OocPresetDocument[] = [
  { id: "protect-player-agency", title: "คุมตัวละครผู้เล่น", text: "(OOC: หลีกเลี่ยงการเล่นเป็นตัวละครผู้เล่น หลีกเลี่ยงการพูดแทนตัวละครผู้เล่น และหลีกเลี่ยงการตัดสินใจการกระทำของตัวละครผู้เล่น ตัวละครนี้คืออำนาจการ role play ของผู้เล่น ส่วน GM ให้ role play โลกและ NPC)" },
  { id: "immersive-format", title: "โฟกัสรูปแบบและบรรยาย", text: "(OOC: บรรยายเป็นร้อยแก้ว โดยระบบแอพเป็นผู้แสดง Header บรรยายอย่างดื่มด่ำ เห็นภาพสถานที่และบรรยากาศ ไม่เร่งรีบ ใช้ show not tell ให้ความสำคัญกับผู้คนรอบข้าง เสียง และรายละเอียดของฉาก โดยรักษาความสมจริงตามบริบท)" },
  { id: "third-person-cut", title: "สลับมุมมองบุคคลที่ 3", text: "(OOC: สลับมุมมองเดินเรื่องผ่านมุมมองบุคคลที่ 3 ของตัวละครอื่นหรือตัวละครใหม่ เพื่อให้เรื่องมีมิติ)" },
  { id: "no-omniscient-npc", title: "ห้าม NPC รู้ทุกอย่าง", text: "(OOC: ห้ามตัวละครอื่นรับรู้ข้อมูลที่พวกเขาไม่อาจรู้ ไม่เห็น หรือไม่เคยได้ยิน เช่น ชื่อ สกิล ความลับ และความรู้ที่ไม่เข้ากับยุคสมัยของเรื่อง)" },
];

export function activeStoryMessageBody(message: StoryMessageDocument): string {
  return message.variants[message.activeVariantIndex] || message.body;
}

export function nextStoryMessageId(messages: readonly StoryMessageDocument[]): number {
  return Math.max(0, ...messages.map((message) => Number.isFinite(message.id) ? message.id : 0)) + 1;
}

export function storyMessagesToConversation(messages: readonly StoryMessageDocument[], sessionId: string): ConversationMessage[] {
  return messages.map((message, index) => ({
    id: `${sessionId}:${message.id}`,
    sourceMessageId: message.id,
    sequence: index + 1,
    role: message.role === "MEDIA" ? "media" : message.role === "AI" ? "assistant" : "user",
    content: activeStoryMessageBody(message),
    enabled: message.enabled,
    variants: [...message.variants],
    activeVariantIndex: message.activeVariantIndex,
    contextExcluded: message.contextExcluded,
    generationPurpose: message.generationPurpose,
  }));
}

export function conversationToStoryMessages(messages: readonly ConversationMessage[]): StoryMessageDocument[] {
  let nextId = Math.max(0, ...messages.map((message) => message.sourceMessageId ?? 0)) + 1;
  return messages.filter((message) => message.role !== "system").map((message) => {
    const body = message.content;
    const variants = message.variants?.length ? [...message.variants] : body ? [body] : [];
    const activeVariantIndex = Math.max(0, Math.min(message.activeVariantIndex ?? Math.max(0, variants.length - 1), Math.max(0, variants.length - 1)));
    return {
      id: message.sourceMessageId ?? nextId++,
      role: message.role === "media" ? "MEDIA" : message.role === "assistant" ? "AI" : "PLAYER",
      body,
      enabled: message.enabled !== false,
      variants,
      activeVariantIndex,
      contextExcluded: message.contextExcluded === true,
      generationPurpose: message.generationPurpose || "story",
    };
  });
}

export function storyMessagesForGateway(messages: readonly StoryMessageDocument[]): { role: "user" | "assistant"; content: string }[] {
  return messages
    .filter((message) => message.enabled && !message.contextExcluded && message.role !== "MEDIA")
    .map((message) => ({ role: message.role === "AI" ? "assistant" as const : "user" as const, content: activeStoryMessageBody(message) }))
    .filter((message) => message.content.trim().length > 0);
}

export function appendPlayerStoryMessage(messages: readonly StoryMessageDocument[], body: string): StoryMessageDocument[] {
  const text = body.trim();
  if (!text) return [...messages];
  return [...messages, { id: nextStoryMessageId(messages), role: "PLAYER", body: text, enabled: true, variants: [text], activeVariantIndex: 0, contextExcluded: false, generationPurpose: "story" }];
}

export function appendNarratorStoryMessage(messages: readonly StoryMessageDocument[], body: string, generationPurpose = "story", previousVariants: readonly string[] = []): StoryMessageDocument[] {
  const variants = [...previousVariants, body];
  return [...messages, { id: nextStoryMessageId(messages), role: "AI", body, enabled: true, variants, activeVariantIndex: Math.max(0, variants.length - 1), contextExcluded: false, generationPurpose }];
}

export interface StoryRegenerationPlan {
  messages: StoryMessageDocument[];
  previousVariants: string[];
  generationPurpose: string;
  temporaryPrompt: string;
  removedMessageId: number;
}

export function prepareStoryRegeneration(messages: readonly StoryMessageDocument[], instruction = ""): StoryRegenerationPlan | null {
  const index = [...messages].map((message, itemIndex) => ({ message, itemIndex })).reverse()
    .find(({ message }) => message.role === "AI" && message.generationPurpose !== "opening_seed")?.itemIndex ?? -1;
  if (index < 0) return null;
  const removed = messages[index]!;
  const previousVariants = removed.variants.length ? [...removed.variants] : removed.body ? [removed.body] : [];
  const continued = removed.generationPurpose === "continue" || removed.generationPurpose === "cut_recovery";
  const basePrompt = continued ? gameplayPrompts.continue : gameplayPrompts.regenerate;
  const temporaryPrompt = instruction.trim() ? `[HIGH PRIORITY FOR THIS REGENERATION]\n${instruction.trim()}\n\n${basePrompt}` : basePrompt;
  return { messages: messages.filter((_, itemIndex) => itemIndex !== index), previousVariants, generationPurpose: continued ? "continue" : "story", temporaryPrompt, removedMessageId: removed.id };
}

export function rewindStoryMessages(messages: readonly StoryMessageDocument[]): { messages: StoryMessageDocument[]; draftInput: string; removed: StoryMessageDocument | null } {
  const removed = messages.at(-1) ?? null;
  if (!removed) return { messages: [], draftInput: "", removed: null };
  return { messages: messages.slice(0, -1), draftInput: removed.role === "PLAYER" ? activeStoryMessageBody(removed) : "", removed };
}

export function changeStoryMessageVariant(messages: readonly StoryMessageDocument[], messageId: number, direction: number): StoryMessageDocument[] {
  return messages.map((message) => {
    if (message.id !== messageId || message.variants.length < 2) return message;
    const activeVariantIndex = Math.max(0, Math.min(message.activeVariantIndex + Math.sign(direction), message.variants.length - 1));
    return { ...message, activeVariantIndex, body: message.variants[activeVariantIndex] ?? message.body };
  });
}

export function toggleStoryMessage(messages: readonly StoryMessageDocument[], messageId: number): StoryMessageDocument[] {
  return messages.map((message) => message.id === messageId ? { ...message, enabled: !message.enabled } : message);
}

export function editStoryMessage(messages: readonly StoryMessageDocument[], messageId: number, body: string): StoryMessageDocument[] {
  return messages.map((message) => {
    if (message.id !== messageId) return message;
    if (message.role !== "AI" || message.generationPurpose === "opening_seed" || !message.variants.length) return { ...message, body, variants: body ? [body] : [] };
    const variants = [...message.variants];
    const active = Math.max(0, Math.min(message.activeVariantIndex, variants.length - 1));
    variants[active] = body;
    return { ...message, body, variants, activeVariantIndex: active };
  });
}
